<template>
  <div class="calendar-page" :class="{ 'has-bottom-bar': hasBottomBar }">
    <SessionExpiredModal />
    <OldActiveTimerModal />
    <TimerModal />
    <FocusMode v-if="store.focusModeOpen" :timer-id="store.focusModeTimerId" @close="store.closeFocusMode()" />
    <PreferencesModal />
    <OnboardingTour />

    <!-- Sync Overlay -->
    <div v-if="isSyncing" class="modal is-active">
      <div class="modal-background"></div>
      <div class="modal-content has-text-centered">
        <div class="box">
          <span class="icon is-large">
            <FontAwesomeIcon :icon="faRotate" />
          </span>
          <p class="mt-2">Syncing your timers...</p>
        </div>
      </div>
    </div>

    <div class="calendar-page__content">
      <header ref="stickyHeader" class="calendar-sticky">
        <div class="calendar-sticky__layer">
          <NavBar />
        </div>

        <div class="calendar-sticky__layer">
          <AppBanner />
          <div class="calendar-header">
            <div class="calendar-header__date has-text-centered">
              <h2 class="title is-4 is-hidden-mobile">{{ formattedDate }}</h2>
              <h2 class="title is-6 is-hidden-tablet">{{ formattedDate }}</h2>
            </div>
            <div class="calendar-header__wrapper">
              <div class="calendar-header__form">
                <AddTimer />
              </div>
            </div>
          </div>
        </div>
      </header>

      <main class="calendar-main">
        <div ref="calendarArea" class="scheduling-calendar">
            <div class="time-list has-text-right">
              <div class="time-list__now-anchor">
                <button
                  class="button is-small scroll-to-now-button"
                  :class="{ 'is-active': !isCurrentTimeVisible && isCurrentDateInView }"
                  :aria-label="scrollToNowLabel"
                  @click="scrollToCurrentTime"
                >
                  <span v-if="currentTimeDirection === 'up'" class="scroll-to-now-button__chevron">
                    <FontAwesomeIcon :icon="faChevronUp" />
                  </span>
                  <span class="scroll-to-now-button__letters" aria-hidden="true">
                    <span>N</span>
                    <span>O</span>
                    <span>W</span>
                  </span>
                  <span v-if="currentTimeDirection === 'down'" class="scroll-to-now-button__chevron">
                    <FontAwesomeIcon :icon="faChevronDown" />
                  </span>
                </button>
              </div>

              <div
                v-for="hour in hours"
                :key="`time-${hour.raw}`"
                class="time-entry"
                :style="{ height: `${hourBlockHeight}px` }"
              >
                <span v-html="hour.display"></span>
              </div>
            </div>

            <div class="schedule-grid" :style="gridStyle">
              <div
                v-for="hour in hours"
                :key="`marker-${hour.raw}`"
                class="hour-marker"
                :style="{ top: `${(hour.raw - hours[0].raw) * hourBlockHeight + 20}px` }"
              ></div>

              <div ref="currentTimeLine" class="current-time-line" :style="currentTimeLineStyle" v-if="isCurrentDateInView"></div>

              <EventItem
                v-for="event in allEvents"
                :key="`event-${event.id}`"
                :event="event"
                :displayStart="displayStart"
              />

              <BlockItem 
                v-for="(block, index) in blocks" 
                :key="block.id" 
                :block="block" 
                :displayStart="displayStart"
                :data-onboarding-target="index === 0 ? 'timer-block' : undefined"
              />

              <ContextBar
                v-if="store.hasContextTimer"
                :displayStart="displayStart"
                @context-click="openContextModal"
              />
            </div>
        </div>

        <div v-if="shouldShowSessionRenewal" class="calendar-renewal">
          <div class="notification p-3">
            <div class="columns is-vcentered">
              <div class="column">
                <p>Your session is more than 24 hours old. Would you like to start a new day?</p>
              </div>
              <div class="column is-narrow">
                <button class="button is-primary" @click="handleSessionRenewal">
                  Start New Day
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="isDevelopment()" class="mt-5">
          <DebugView :displayStart="displayStart" />
        </div>
      </main>

      <Footer />
      <BottomControlBar />
    </div>

    <AnalyticsSideTabs
      v-if="!isFrontendOnly && store.hasAnalytics"
      :historyOpen="isHistoryOpen"
      :analyticsOpen="isAnalyticsOpen"
      @update:historyOpen="isHistoryOpen = $event"
      @update:analyticsOpen="isAnalyticsOpen = $event"
    />
    <button v-else-if="!isFrontendOnly" class="button history-button is-hidden-touch" @click="showHistory">
      <span class="icon">
        <FontAwesomeIcon :icon="faHistory" />
      </span>
      <span>History</span>
    </button>

    <HistoricalTimers
      :is-open="isHistoryOpen"
      @close="closeAllDrawers"
    />

    <AnalyticsPanel
      :is-open="isAnalyticsOpen"
      @close="closeAnalytics"
    />

    <ContextModal
      :isOpen="contextModalOpen"
      :context="contextModalContext"
      @close="contextModalOpen = false"
      @saved="contextModalOpen = false"
      @deleted="contextModalOpen = false"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick, provide } from 'vue';
import { storeToRefs } from 'pinia';
import { useBlocksStore } from '../stores/blocks';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faRotate, faHistory, faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons'
import AnalyticsSideTabs from '../components/AnalyticsSideTabs.vue';
import AnalyticsPanel from '../components/AnalyticsPanel.vue';
import { isDevelopment } from '../utils/environment';
import { formatLocalizedTime, formatFullDate } from '../utils/time';
import { usePageFocus } from '../composables/usePageFocus';

import NavBar from '../components/NavBar.vue';
import AppBanner from '../components/AppBanner.vue';
import AddTimer from '../components/AddTimer.vue';
import SessionExpiredModal from '../components/SessionExpiredModal.vue';
import OldActiveTimerModal from '../components/OldActiveTimerModal.vue';
import OnboardingTour from '../components/OnboardingTour.vue';
import BlockItem from '../components/BlockItem.vue'; // Import BlockItem component
import EventItem from '../components/EventItem.vue'; // Import EventItem component
import FocusMode from '../components/FocusMode.vue';
import TimerModal from '../components/TimerModal.vue';
import PreferencesModal from '../components/PreferencesModal.vue';
import Footer from '../components/Footer.vue';
import BottomControlBar from '../components/BottomControlBar.vue';
import HistoricalTimers from '../components/HistoricalTimers.vue';
import ContextBar from '../components/ContextBar.vue';
import ContextModal from '../components/ContextModal.vue';
import DebugView from '../components/DebugView.vue';

const store = useBlocksStore();

const { blocks, startTime, userSession, isFrontendOnly } = storeToRefs(store);
const allEvents = computed(() => store.allEvents);
const hasBottomBar = computed(() => !!(store.activeTimer || store.currentlyDueTimer || store.hasContextTimer));
const isSyncing = ref(false);
const isHistoryOpen = ref(false);
const isAnalyticsOpen = ref(false);
const contextModalOpen = ref(false);
const contextModalContext = ref(null);
const stickyHeader = ref(null);
const calendarArea = ref(null);
const currentTimeLine = ref(null);
const isCurrentTimeVisible = ref(true);
const currentTimeDirection = ref('down');
let scrollThrottleTimeout = null;

// Mobile breakpoint detection (Bulma touch breakpoint: ≤768px)
const isMobile = ref(false);
let mobileMediaQuery = null;
const updateMobileState = () => {
  isMobile.value = window.matchMedia('(max-width: 768px)').matches;
};

const showHistory = () => {
  isHistoryOpen.value = true;
};

const closeAllDrawers = () => {
  isHistoryOpen.value = false;
  isAnalyticsOpen.value = false;
};

const openAnalytics = () => {
  if (isMobile.value) {
    // Mobile: mutually exclusive drawers - analytics only
    isHistoryOpen.value = false;
    isAnalyticsOpen.value = true;
  } else {
    // Desktop: both drawers side-by-side
    isHistoryOpen.value = true;
    isAnalyticsOpen.value = true;
  }
};

const closeAnalytics = () => {
  if (isMobile.value) {
    // Mobile: fall back to history
    isAnalyticsOpen.value = false;
    isHistoryOpen.value = true;
  } else {
    // Desktop: just close analytics
    isAnalyticsOpen.value = false;
  }
};

provide('openAnalytics', openAnalytics);

const openContextModal = (ctx) => {
  contextModalContext.value = ctx;
  contextModalOpen.value = true;
};

const SLOT_HEIGHT = 10;
const HOUR_BLOCK_HEIGHT = SLOT_HEIGHT * 12;
const hourBlockHeight = HOUR_BLOCK_HEIGHT;

onMounted(async () => {
  // Initialize the app
  store.initializeStartTime();
  store.pushLocalBlocksToRemote();

  // Initialize mobile state and listen for changes
  updateMobileState();
  mobileMediaQuery = window.matchMedia('(max-width: 768px)');
  mobileMediaQuery.addEventListener('change', updateMobileState);

  await nextTick();
  checkCurrentTimeVisibility();
  window.addEventListener('scroll', handleScroll, { passive: true });
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
  if (mobileMediaQuery) {
    mobileMediaQuery.removeEventListener('change', updateMobileState);
  }
  if (scrollThrottleTimeout) {
    clearTimeout(scrollThrottleTimeout);
    scrollThrottleTimeout = null;
  }
});

const displayStart = computed(() => {
  // Anchor the calendar to the current session date so we don't stay on a stale day
  const baseDate = userSession.value?.initializedAt
    ? new Date(userSession.value.initializedAt)
    : new Date();

  if (startTime.value !== null) {
    return new Date(baseDate.setHours(0, startTime.value, 0, 0));
  }
  return baseDate;
});

const displayEnd = computed(() => new Date(displayStart.value.getTime() + 30 * 60 * 60 * 1000));

const hours = computed(() => {
  const startHour = displayStart.value.getHours();

  return Array.from({ length: 30 }, (_, i) => {
    const hour = startHour + i;
    const date = new Date(displayStart.value);
    date.setHours(hour % 24);
    const displayHour = formatLocalizedTime(date);
    const dayIndicator = hour >= 24 ? '<sup class="day-indicator has-text-secondary">(+1) </sup>' : '';

    return {
      raw: hour,
      display: `${dayIndicator}${displayHour}`
    };
  });
});

const totalSlots = computed(() => (displayEnd.value - displayStart.value) / (5 * 60 * 1000));

const gridStyle = computed(() => ({
  gridTemplateRows: `repeat(${totalSlots.value}, ${SLOT_HEIGHT}px)`,
  height: 'auto',
}));

const currentTimeLineStyle = computed(() => {
  const now = store.currentTime;
  const minutesSinceDisplayStart =
    (now.getHours() * 60 + now.getMinutes()) -
    (displayStart.value.getHours() * 60 + displayStart.value.getMinutes());

  const topPosition = (minutesSinceDisplayStart / 5) * SLOT_HEIGHT + 15; // 5min slots

  return {
    top: `${topPosition}px`,
    width: '100%',
    position: 'absolute',
    zIndex: 10,
    display: minutesSinceDisplayStart >= 0 && minutesSinceDisplayStart < 30 * 60 ? 'block' : 'none'
  };
});

const isCurrentDateInView = computed(() => {
  const now = store.currentTime;
  return now >= displayStart.value && now < displayEnd.value;
});

const scrollToNowLabel = computed(() => {
  return currentTimeDirection.value === 'up'
    ? 'Scroll up to current time'
    : 'Scroll down to current time';
});

// Scroll window so the current time is centered beneath the sticky header
const scrollToCurrentTime = async () => {
  await nextTick();
  if (!calendarArea.value || startTime.value === null || typeof window === 'undefined') return;

  const now = store.currentTime;
  const currentHour = now.getHours();
  const displayStartHour = displayStart.value.getHours();

  // Calculate hours since display start
  const hoursSinceStart = (currentHour - displayStartHour + 30) % 30;
  const scrollHour = Math.max(0, hoursSinceStart - 1);
  const scrollPosition = scrollHour * HOUR_BLOCK_HEIGHT;

  const calendarTop = calendarArea.value.getBoundingClientRect().top + window.scrollY;
  const stickyOffset = stickyHeader.value?.offsetHeight || 0;

  window.scrollTo({
    top: Math.max(calendarTop + scrollPosition - stickyOffset, 0),
    behavior: 'smooth'
  });
  checkCurrentTimeVisibility();
};

const checkCurrentTimeVisibility = () => {
  if (!isCurrentDateInView.value || !currentTimeLine.value) {
    isCurrentTimeVisible.value = true;
    currentTimeDirection.value = 'down';
    return;
  }

  const rect = currentTimeLine.value.getBoundingClientRect();
  const stickyOffset = stickyHeader.value?.offsetHeight || 0;
  const viewportHeight = window.innerHeight;

  const isAboveViewport = rect.bottom < stickyOffset;
  const isBelowViewport = rect.top > viewportHeight;

  isCurrentTimeVisible.value = !isAboveViewport && !isBelowViewport;
  currentTimeDirection.value = isAboveViewport ? 'up' : 'down';
};

const handleScroll = () => {
  if (scrollThrottleTimeout) return;

  scrollThrottleTimeout = window.setTimeout(() => {
    scrollThrottleTimeout = null;
    checkCurrentTimeVisibility();
  }, 150);
};

usePageFocus(() => checkCurrentTimeVisibility());

// Add computed property
const debugStore = computed(() => ({
  blocks: store.blocks,
  events: store.events, // Raw events from API
  allEvents: store.allEvents, // Combined events including active ones
  userSession: store.userSession,
  isFrontendOnly: store.isFrontendOnly,
  currentTime: store.currentTime,
  startTime: store.startTime,
  displayStart: displayStart.value,
}));

const formattedDate = computed(() => {
  const date = userSession.value?.initializedAt || startTime.value;
  return formatFullDate(date || new Date());
});

// Add new computed property to check session age
const shouldShowSessionRenewal = computed(() => {
  if (!userSession.value?.initializedAt || userSession.value.local) return false;
  const now = new Date();
  const sessionAge = now - userSession.value.initializedAt;
  return sessionAge > 24 * 60 * 60 * 1000; // 24 hours in milliseconds
});

// Add handler for session renewal
const handleSessionRenewal = async () => {
  await store.renewSession();
};

// Watch for startTime initialization
watch(
  startTime,
  (newStartTime) => {
    if (newStartTime !== null) {
      scrollToCurrentTime();
    }
  },
  { immediate: true }
);

// Also scroll when the session day changes (e.g. after renewing a stale session)
watch(
  () => userSession.value?.initializedAt,
  () => {
    if (startTime.value !== null) {
      scrollToCurrentTime();
    }
  }
);

// Update document title with formatted session date
watch(
  formattedDate,
  (newDate) => {
    document.title = `Timist - ${newDate}`;
  },
  { immediate: true }
);
</script>

<style>
/* CSS Variables */
.calendar-page {
  --calendar-max-width: 960px;
  --calendar-sidebar-width: 85px;
  --calendar-gap: 1rem;

  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--bulma-scheme-main);
}

.calendar-page__content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* Sticky Header */
.calendar-sticky {
  position: sticky;
  top: 0;
  z-index: 20;
  background: var(--bulma-scheme-main);
  overflow-anchor: none;
  overflow: visible;
}

.calendar-sticky__layer + .calendar-sticky__layer {
  border-top: 1px solid rgba(0, 0, 0, 0.04);
}

/* Shared centered container pattern */
.calendar-header,
.scheduling-calendar,
.calendar-renewal {
  max-width: var(--calendar-max-width);
  margin-left: auto;
  margin-right: auto;
  padding-left: var(--calendar-gap);
  padding-right: var(--calendar-gap);
}

/* Header */
.calendar-header {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--calendar-gap);
  padding-top: 1rem;
  padding-bottom: 0.5rem;
}

.calendar-header__date .title {
  font-weight: 500;
  margin-bottom: 0;
}

.calendar-header__wrapper {
  display: grid;
  grid-template-columns: var(--calendar-sidebar-width) 1fr;
  gap: var(--calendar-gap);
}

.calendar-header__form {
  grid-column: 2;
  position: relative;
  min-height: 3rem;
  z-index: 3;
}

.calendar-header__form > * {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 2;
}

.calendar-demo-notice .notification {
  margin-bottom: 0;
}

/* Main Content */
.calendar-main {
  flex: 1;
  padding-top: 0.75rem;
  padding-bottom: 2rem;
}

.has-bottom-bar .calendar-main {
  padding-bottom: max(5rem, calc(4rem + env(safe-area-inset-bottom)));
}

.scheduling-calendar {
  display: grid;
  grid-template-columns: var(--calendar-sidebar-width) 1fr;
  gap: var(--calendar-gap);
  align-items: flex-start;
  scroll-margin-top: 200px;
}

/* Time List */
.time-list {
  position: relative;
  padding-top: 0.55rem;
}

.time-list__now-anchor {
  position: sticky;
  top: calc(50vh - 3.25rem);
  height: 0;
  z-index: 12;
  overflow: visible;
}

.time-entry {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding-right: 0.5rem;
  font-size: 0.95rem;
  color: var(--bulma-text);
}

/* Schedule Grid */
.schedule-grid {
  position: relative;
  display: grid;
  background-color: var(--bulma-scheme-main-bis);
  border-radius: var(--bulma-radius-large);
  padding: 1rem;
}

.hour-marker {
  position: absolute;
  left: 0;
  width: 100%;
  border-top: 1px solid var(--bulma-scheme-main-ter);
  pointer-events: none;
  z-index: 1;
}

.current-time-line {
  height: 2px;
  background-color: var(--bulma-link);
  pointer-events: none;
  box-shadow: 0 0 2px rgba(var(--bulma-link-rgb), 0.5);
}

.day-indicator {
  font-size: 0.7em;
  margin-left: 2px;
}

/* Session Renewal */
.calendar-renewal .notification {
  margin-top: 1.5rem;
}

/* History Button */
.history-button {
  position: fixed;
  left: 0;
  top: 50%;
  transform: translateY(-50%) rotate(-90deg);
  transform-origin: left top;
  z-index: 30;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-top: 0;
}

.scroll-to-now-button {
  position: absolute;
  top: 0;
  left: -2.75rem;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.18rem;
  min-width: 2.1rem;
  min-height: 4.25rem;
  padding: 0.4rem 0.3rem;
  border: none;
  background: var(--bulma-scheme-main-bis);
  color: var(--bulma-text-weak);
  z-index: 12;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  box-shadow: none;
  transition: opacity 0.2s ease, visibility 0.2s ease, color 0.2s ease, background-color 0.2s ease;
  border-radius: 999px;
}

.scroll-to-now-button:hover {
  background: var(--bulma-scheme-main-ter);
  color: var(--bulma-text-strong);
}

.scroll-to-now-button__letters {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 0.08rem;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1;
}

.scroll-to-now-button__chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
}

.scroll-to-now-button.is-active {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}

/* Mobile */
@media (max-width: 768px) {
  .calendar-page {
    --calendar-sidebar-width: 52px;
  }

  .calendar-header__wrapper,
  .scheduling-calendar {
    grid-template-columns: var(--calendar-sidebar-width) 1fr;
  }

  .time-entry {
    font-size: 0.9rem;
  }

  .time-list__now-anchor {
    top: calc(50vh - 2.75rem);
  }

  .scroll-to-now-button {
    left: -1.35rem;
    min-width: 1.8rem;
    min-height: 3.85rem;
    padding: 0.3rem 0.2rem;
  }

  .scroll-to-now-button__letters {
    font-size: 0.68rem;
  }

  .calendar-header {
    padding-top: 0.5rem;
    padding-bottom: 0.75rem;
  }

  .calendar-header__form {
    min-height: 3rem;
  }
}
</style>